import React from 'react';
import { Link } from 'react-router-dom';
import { Download, Calendar, Heart, MessageCircle, Smartphone, Shield, ChevronRight, Check, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import CycleCalculator from '../components/CycleCalculator';
import FeaturesCarousel from '../components/FeaturesCarousel';
import Testimonials from '../components/Testimonials';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';

const Home = () => {
  const features = [
    {
      icon: Calendar,
      title: "AI-Powered Cycle Predictions",
      description: "Smart algorithm learns your unique cycle pattern for accurate predictions"
    },
    {
      icon: Heart,
      title: "Personalized Health Plans",
      description: "Get customized wellness recommendations based on your cycle phase"
    },
    {
      icon: MessageCircle,
      title: "Expert Consultations",
      description: "Connect with healthcare professionals for personalized guidance"
    },
    {
      icon: Smartphone,
      title: "Voice AI Assistant",
      description: "Log symptoms and get insights through natural conversation"
    },
    {
      icon: MessageCircle,
      title: "WhatsApp Health Bot",
      description: "Receive reminders and tips directly on WhatsApp"
    },
    {
      icon: Shield,
      title: "Wearable Device Sync",
      description: "Integrate with your fitness tracker for comprehensive health data"
    }
  ];

  const yogaPoses = {
    menstrual: [
      { name: "Child's Pose (Balasana)", benefit: "Relieves cramps and back pain" },
      { name: "Reclining Bound Angle", benefit: "Opens hips, reduces tension" },
      { name: "Cat-Cow Stretch", benefit: "Gentle spinal movement" }
    ],
    follicular: [
      { name: "Warrior II", benefit: "Builds strength and confidence" },
      { name: "Tree Pose", benefit: "Improves balance and focus" },
      { name: "Sun Salutations", benefit: "Energizing full-body flow" }
    ],
    ovulation: [
      { name: "Goddess Pose", benefit: "Celebrates feminine power" },
      { name: "Bridge Pose", benefit: "Opens heart and hips" },
      { name: "Camel Pose", benefit: "Energetic back bend" }
    ],
    luteal: [
      { name: "Forward Fold", benefit: "Calming and grounding" },
      { name: "Legs Up the Wall", benefit: "Reduces bloating and anxiety" },
      { name: "Supine Twist", benefit: "Digestive relief" }
    ]
  };

  const faqs = [
    {
      question: "How does Ovalu predict my period?",
      answer: "Ovalu uses your logged period data to calculate average cycle length, then predicts future dates using the formula: Next Period = Last Period Start + Average Cycle Length."
    },
    {
      question: "How is ovulation calculated?",
      answer: "Ovulation is estimated at Cycle Length minus 14 days from the start of your last period. The fertile window spans ovulation ± 2 days."
    },
    {
      question: "Is my data secure?",
      answer: "Yes, all your data is encrypted and stored securely. We follow strict privacy protocols and never share your personal health information with third parties."
    },
    {
      question: "Can I sync with wearable devices?",
      answer: "Absolutely! Ovalu integrates with popular fitness trackers to provide comprehensive health insights by combining cycle data with activity, sleep, and heart rate metrics."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5F8] to-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block bg-pink-50 px-4 py-2 rounded-full">
                <span className="text-[#FF6B9D] font-medium text-sm">✨ Empower Your Health</span>
              </div>

              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                Know Your Cycle.<br />
                <span className="text-[#FF6B9D]">Empower Your Health.</span>
              </h1>

              <p className="text-xl text-gray-600 leading-relaxed">
                Take control of your reproductive health with AI-powered cycle tracking,
                personalized insights, and expert consultations.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                {/* <Button
                  onClick={() => {
                    const featuresSection = document.querySelector('#features');
                    if (featuresSection) {
                      featuresSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className="bg-[#FF6B9D] hover:bg-[#ff5a8c] text-white rounded-full px-8 py-6 text-lg group"
                >
                  Download Now
                  <Download className="ml-2 w-5 h-5 group-hover:translate-y-1 transition-transform" />
                </Button> */}
                <Button
                  onClick={() => {
                    const featuresSection = document.querySelector('#features');
                    if (featuresSection) {
                      featuresSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  variant="outline"
                  className="border-[#FF6B9D] text-[#FF6B9D] hover:bg-pink-50 rounded-full px-8 py-6 text-lg"
                >
                  Learn More
                </Button>
              </div>

              <div className="flex items-center space-x-6 pt-6">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFB6C1] to-[#FF6B9D] border-2 border-white" />
                  ))}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Trusted by <strong className="text-gray-900">50,000+</strong> women</p>
                </div>
              </div>
            </div>

            <Link to="/cycle-calculator" className="relative block group transition-transform duration-500 hover:scale-[1.02]">
              <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B9D]/20 to-[#FFB6C1]/20 rounded-[3rem] blur-3xl group-hover:blur-2xl transition-all" />
              <div className="relative bg-white/60 backdrop-blur-sm p-8 rounded-[3rem] border border-pink-100 shadow-2xl group-hover:border-[#FF6B9D]/30 transition-colors">
                <div className="w-full h-96 bg-gradient-to-br from-[#FF6B9D] to-[#FFB6C1] rounded-3xl flex items-center justify-center overflow-hidden relative">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-from)_0%,_transparent_70%)] opacity-20 group-hover:scale-150 transition-transform duration-1000" />
                  <div className="text-center text-white space-y-4 relative z-10">
                    <div className="w-32 h-32 mx-auto bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center group-hover:rotate-12 transition-transform duration-500">
                      <Calendar className="w-16 h-16" />
                    </div>
                    <h3 className="text-2xl font-bold">Track Your Cycle</h3>
                    <p className="text-white/80">Beautiful & Intuitive</p>
                    <div className="pt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-sm bg-white/20 px-4 py-1 rounded-full backdrop-blur-sm">Try it now →</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Premium Features for Your Wellness
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to understand and manage your reproductive health
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="p-6 border-pink-100 hover:border-[#FF6B9D] transition-all duration-300 hover:shadow-lg group cursor-pointer">
                <div className="w-14 h-14 bg-gradient-to-br from-[#FF6B9D]/10 to-[#FFB6C1]/10 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-7 h-7 text-[#FF6B9D]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#FFF5F8] to-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How Ovalu Works
            </h2>
            <p className="text-xl text-gray-600">
              Simple steps to take control of your health
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Track Your Cycle", desc: "Log your period dates and symptoms in seconds" },
              { step: "2", title: "Get AI Insights", desc: "Receive personalized predictions and health tips" },
              { step: "3", title: "Stay Empowered", desc: "Make informed decisions about your health" }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="text-center space-y-4">
                  <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#FF6B9D] to-[#FFB6C1] rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                    {item.step}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-10 -right-4 text-[#FFB6C1]">
                    <ChevronRight className="w-8 h-8" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Features Carousel */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <div className="inline-block bg-pink-50 px-6 py-3 rounded-full mb-6">
              <span className="text-[#FF6B9D] font-bold text-sm">🚀 COMPLETE FEATURE LIST</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Everything You Need in <span className="text-[#FF6B9D]">One App</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From AI-powered predictions to expert consultations, pregnancy tracking to mental wellness -
              Ovalu is your complete women's health companion
            </p>
          </div>

          <FeaturesCarousel />

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">Ready to experience the future of women's health?</p>
            <Button
              onClick={() => window.open('https://play.google.com/store', '_blank')}
              className="bg-[#FF6B9D] hover:bg-[#ff5a8c] text-white rounded-full px-8 py-6 text-lg"
            >
              <Download className="w-5 h-5 mr-2" />
              Download Ovalu Now
            </Button>
          </div>
        </div>
      </section>

      {/* Cycle Calculator Section */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <CycleCalculator />
        </div>
      </section>

      {/* Yoga for Your Cycle Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#FFF5F8] to-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Yoga for Every Phase of Your Cycle
            </h2>
            <p className="text-xl text-gray-600">
              Align your practice with your body's natural rhythm
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(yogaPoses).map(([phase, poses]) => (
              <Card key={phase} className="p-6 border-pink-100 hover:shadow-lg transition-all duration-300">
                <div className="mb-4">
                  <div className={`inline-block px-4 py-2 rounded-full text-sm font-semibold mb-3 ${phase === 'menstrual' ? 'bg-red-100 text-red-700' :
                    phase === 'follicular' ? 'bg-green-100 text-green-700' :
                      phase === 'ovulation' ? 'bg-purple-100 text-purple-700' :
                        'bg-blue-100 text-blue-700'
                    }`}>
                    {phase.charAt(0).toUpperCase() + phase.slice(1)} Phase
                  </div>
                </div>

                <div className="space-y-4">
                  {poses.map((pose, index) => (
                    <div key={index} className="border-l-4 border-[#FF6B9D] pl-4">
                      <h4 className="font-semibold text-gray-900 mb-1">{pose.name}</h4>
                      <p className="text-sm text-gray-600">{pose.benefit}</p>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <div className="inline-block bg-pink-50 px-6 py-3 rounded-full mb-6">
              <span className="text-[#FF6B9D] font-bold text-sm">⭐ TRUSTED BY THOUSANDS</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our Users Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join thousands of women who have transformed their health journey with Ovalu
            </p>
          </div>

          <Testimonials />

          <div className="text-center mt-16">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-50 to-purple-50 px-8 py-4 rounded-full">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFB6C1] to-[#FF6B9D] border-2 border-white" />
                ))}
              </div>
              <div className="text-left pl-3">
                <p className="text-sm font-semibold text-gray-900">50,000+ Happy Users</p>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="text-sm text-gray-600">4.9/5 Average Rating</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about Ovalu
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border border-pink-100 rounded-2xl px-6 hover:border-[#FF6B9D] transition-colors">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#FF6B9D]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#FF6B9D] to-[#FFB6C1]">
        <div className="container mx-auto max-w-4xl text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Take Control of Your Health?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Join thousands of women who trust Ovalu for their wellness journey
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center">
            <Button
              onClick={() => window.open('https://play.google.com/store', '_blank')}
              className="bg-white text-[#FF6B9D] hover:bg-gray-50 rounded-full px-8 py-6 text-lg font-semibold w-full sm:w-[250px]"
            >
              Download for Android
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
